import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import './ImageCarousel.css';

function ImageCarousel({ images, alt = 'Product image', onImageClick }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, dragFree: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index, e) => {
    e.preventDefault();
    e.stopPropagation();
    emblaApi?.scrollTo(index);
  }, [emblaApi]);

  return (
    <div className="carousel">
      <div className="carousel__container" ref={emblaRef}>
        <div className="carousel__track">
          {images.map((src, i) => (
            <div
              key={i}
              className="carousel__image-container"
              onClick={onImageClick ? () => onImageClick(i) : undefined}
              style={onImageClick ? { cursor: 'zoom-in' } : undefined}
            >
              <img
                src={src}
                alt={`${alt} ${i + 1}`}
                className="carousel__image"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      {images.length > 1 && (
        <button
          className="carousel__button carousel__button--prev"
          onClick={scrollPrev}
          aria-label="Previous image"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {images.length > 1 && (
        <button
          className="carousel__button carousel__button--next"
          onClick={scrollNext}
          aria-label="Next image"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {images.length > 1 && (
        <div className="carousel__dots">
          {images.map((_, slideIndex) => (
            <button
              key={slideIndex}
              className={`carousel__dot ${slideIndex === selectedIndex ? 'active' : ''}`}
              onClick={(e) => scrollTo(slideIndex, e)}
              aria-label={`Go to image ${slideIndex + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ImageCarousel;
